import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, Route, Routes } from 'react-router-dom';

import { CreditLimits } from './components/credit-limits';
import { Integrations } from './components/integrations';
import { Roles } from './components/roles';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

enum ConfigurationTab {
  Credits = 'credits',
  Integrations = 'integrations',
  Roles = 'roles',
}

interface OrgTab {
  id: string;
  permission: Permission;
  title: string;
  icon: React.ReactNode;
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
}

const tabs: OrgTab[] = [
  {
    id: ConfigurationTab.Credits,
    permission: Permission.TokenLimitsManage,
    title: 'Credit Limits',
    icon: <Icon className="lucide--coins" />,
  },
  {
    id: ConfigurationTab.Integrations,
    permission: Permission.IntegrationsManage,
    title: 'Integrations',
    icon: <Icon className="lucide--unplug" />,
  },
  {
    id: ConfigurationTab.Roles,
    permission: Permission.UserManage,
    title: 'Roles & Permissions',
    icon: <Icon className="lucide--shield-user" />,
  },
];

function PermissionTabTrigger({ tab }: { tab: OrgTab }) {
  const { hasPermission } = useHasPermission();
  const allowed = hasPermission(tab.permission);
  const uiMode = tab.uiMode ?? 'hidden';

  if (!allowed && uiMode === 'hidden') return null;

  if (!allowed && uiMode === 'disabled') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <TabsTrigger
              value={tab.id}
              disabled
              // className="flex items-center gap-2  text-base font-medium text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
            >
              {tab.icon}
              {tab.title}
            </TabsTrigger>
          </div>
        </TooltipTrigger>
        <TooltipContent>{tab.disabledMessage ?? 'You do not have permission to access this'}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <TabsTrigger
      value={tab.id}
      // className="flex items-center gap-2 font-serif text-base font-medium text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
    >
      {tab.icon}
      {tab.title}
    </TabsTrigger>
  );
}

function SettingsRedirect() {
  const { hasPermission } = useHasPermission();

  const firstAllowedTab = tabs.find((tab) => hasPermission(tab.permission));

  if (firstAllowedTab) {
    return <Navigate to={firstAllowedTab.id} replace />;
  }

  return <Navigate to="/matters" replace />;
}

export function OrganisationSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get the active tab from the URL
  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment as ConfigurationTab;
  }, [location.pathname]);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          {
            href: '/organisation-settings',
            label: `${capitalizeFirstLetter(t('organisation'))} Settings`,
            active: true,
          },
        ]}
      />
      <PageContainer className="flex min-h-0 flex-1 flex-col">
        <PageHeader>
          <PageTitle
            text={`${capitalizeFirstLetter(t('organisation'))} Settings`}
            subtext={`Manage your ${t('organisation')}'s settings and integrations.`}
          />
        </PageHeader>
        <Tabs className="mb-2" value={activeTab} onValueChange={(tab) => navigate(tab)}>
          <TabsList className="w-full justify-start">
            {tabs.map((tab) => (
              <PermissionTabTrigger key={tab.id} tab={tab} />
            ))}
          </TabsList>
        </Tabs>

        <div className="min-h-0 flex-1">
          <Routes>
            <Route index element={<SettingsRedirect />} />
            <Route path="credits" element={<CreditLimits />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="roles" element={<Roles />} />
          </Routes>
        </div>
      </PageContainer>
    </div>
  );
}
